import React, { useEffect, useState } from "react";
import {
  collection, query, where, getDocs, addDoc, serverTimestamp, doc, getDoc, deleteDoc, setDoc, updateDoc,
} from "firebase/firestore";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { db } from "../../firebase";
import Pagination from "../helpers/Pagination";
import { FaWpforms, FaEdit, FaTrash, FaRegPaperPlane, FaTasks, FaFilePdf, FaPlus, FaSave, FaSync } from "react-icons/fa";

// A reusable component for displaying styled messages
const StatusMessage = ({ message, type }) => {
  if (!message) return null;
  const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
  return (
    <div className={`p-3 rounded-md text-white text-center mb-4 text-sm font-semibold ${bgColor}`}>
      {message}
    </div>
  );
};

const CreateFeedbackForm = () => {
  // --- STATE MANAGEMENT ---
  const [semester, setSemester] = useState("");
  const [section, setSection] = useState("");
  const [subforms, setSubforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [statusSemester, setStatusSemester] = useState("");
  const [statusSection, setStatusSection] = useState("");
  const [students, setStudents] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [showOnlyFilled, setShowOnlyFilled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const [editorCurrentPage, setEditorCurrentPage] = useState(1);
  const QUESTIONS_PER_PAGE_EDITOR = 1;
  
  // State for styled notifications
  const [statusMessage, setStatusMessage] = useState("");
  const [statusMessageType, setStatusMessageType] = useState("success");

  // Helper function to show messages and auto-hide them
  const showMessage = (msg, type = "success", duration = 4000) => {
    setStatusMessage(msg);
    setStatusMessageType(type);
    setTimeout(() => {
      setStatusMessage("");
    }, duration);
  };
  
  // --- LOGIC AND EVENT HANDLERS ---
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const templateRef = doc(db, "feedbackTemplates", "default");
        const templateSnap = await getDoc(templateRef);
        if (templateSnap.exists()) {
          setQuestions(templateSnap.data().questions || []);
        } else {
          console.warn("No default feedback template found.");
        }
      } catch (error) {
        console.error("Failed to fetch questions:", error);
      }
    };
    fetchQuestions();
  }, []);

  const deleteFormAndRelatedData = async (formId) => {
    if (!window.confirm("Are you sure you want to delete this form and all related data?")) return;
    setDeleteLoading(true);
    try {
      const responsesQuery = query(collection(db, "feedbackResponses"), where("feedbackFormId", "==", formId));
      const responsesSnap = await getDocs(responsesQuery);
      for (const docSnap of responsesSnap.docs) {
        await deleteDoc(doc(db, "feedbackResponses", docSnap.id));
      }
      const formDoc = await getDoc(doc(db, "feedbackForms", formId));
      if (formDoc.exists()) {
        const subformsData = formDoc.data().subforms || [];
        for (const { teacherName, subjectCode } of subformsData) {
          const reportRef = doc(db, "feedbackReports", teacherName.trim());
          const reportSnap = await getDoc(reportRef);
          if (reportSnap.exists()) {
            const reportData = reportSnap.data();
            if (reportData.hasOwnProperty(subjectCode)) {
              delete reportData[subjectCode];
              if (Object.keys(reportData).length === 0) await deleteDoc(reportRef);
              else await setDoc(reportRef, reportData);
            }
          }
        }
      }
      const statusQuery = query(collection(db, "feedbackStatus"), where("feedbackFormId", "==", formId));
      const statusSnap = await getDocs(statusQuery);
      for (const docSnap of statusSnap.docs) {
        await deleteDoc(doc(db, "feedbackStatus", docSnap.id));
      }
      await deleteDoc(doc(db, "feedbackForms", formId));
      showMessage("Feedback form and all related data deleted successfully.", "success");
    } catch (error) {
      console.error("Error deleting form and related data:", error);
      showMessage("Failed to delete the form. Check console for details.", "error");
    } finally {
      setDeleteLoading(false);
    }
  };
  
  function loadImageAsDataURL(url) {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.onload = function () {
        const canvas = document.createElement("canvas");
        canvas.width = this.naturalWidth;
        canvas.height = this.naturalHeight;
        canvas.getContext("2d").drawImage(this, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  const handleDownloadReport = async () => {
    try {
      const reportSnapshot = await getDocs(collection(db, "feedbackReports"));
      if (reportSnapshot.empty) {
        return showMessage("No feedback report data found", "error");
      }

      const pdfDoc = new jsPDF();
      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const margin = 5;
      const boxWidth = pageWidth - 2 * margin;
      let y = margin;

      const logoDataUrl = await loadImageAsDataURL("/RV_logo.jpg").catch(
        () => null
      );

      const drawHeader = () => {
        pdfDoc.setDrawColor(180);
        pdfDoc.setLineWidth(0.4);
        pdfDoc.rect(
          margin,
          margin,
          boxWidth,
          pdfDoc.internal.pageSize.getHeight() - 2 * margin,
          "S"
        );

        if (logoDataUrl) {
          pdfDoc.addImage(logoDataUrl, "PNG", margin + 2, y + 2, 20, 20);
        }

        pdfDoc.setFont("helvetica", "bold");
        pdfDoc.setFontSize(14);
        pdfDoc.text(
          "RV Institute of Technology and Management",
          pageWidth / 2,
          y + 10,
          { align: "center" }
        );

        pdfDoc.setFont("helvetica", "normal");
        pdfDoc.setFontSize(10);
        pdfDoc.text("Rashtriya Sikshana Samithi Trust", pageWidth / 2, y + 16, {
          align: "center",
        });
        pdfDoc.text(
          "Department of Computer Science and Engineering",
          pageWidth / 2,
          y + 21,
          { align: "center" }
        );
        pdfDoc.text("Bengaluru - 560076", pageWidth / 2, y + 26, {
          align: "center",
        });

        pdfDoc.setLineWidth(0.5);
        pdfDoc.line(margin + 2, y + 32, pageWidth - margin - 2, y + 32);

        y = 38;
      };

      drawHeader();

      for (const teacherDoc of reportSnapshot.docs) {
        const teacherName = teacherDoc.id;
        const teacherData = teacherDoc.data();

        if (!teacherData || typeof teacherData !== "object") continue;

        pdfDoc.setFont("helvetica", "bold");
        pdfDoc.setFontSize(12);
        pdfDoc.text(`Teacher: ${teacherName}`, margin + 10, y + 10);
        y += 2;

        const headers = [
          [
            "Subject",
            "C1",
            "C2",
            "C3",
            "C4",
            "C5",
            "C6",
            "C7",
            "C8",
            "C9",
            "C10",
            "C11",
            "C12",
            "No. of Students",
            "Total Points",
            "Percentage",
          ],
        ];

        const rows = [];

        for (const [subjectCode, feedback] of Object.entries(teacherData)) {
          if (!feedback || typeof feedback !== "object") continue;

          const {
            total_students = 0,
            total = 0,
            percentage = 0,
            ...criteria
          } = feedback;

          const row = [subjectCode];
          for (let i = 1; i <= 12; i++) {
            const key = `c${i}`;
            row.push(criteria[key] !== undefined ? criteria[key] : 0);
          }
          row.push(total_students, total, `${percentage}%`);
          rows.push(row);
        }

        autoTable(pdfDoc, {
          startY: y + 11,
          head: headers,
          body: rows,
          theme: "grid",
          headStyles: {
            fillColor: [41, 128, 185],
            fontSize: 9,
            halign: "center",
            textColor: [255, 255, 255],
          },
          bodyStyles: {
            fontSize: 9,
            halign: "center",
          },
          margin: { left: margin + 10, right: margin + 10 },
          styles: { cellPadding: 2 },
        });

        y = pdfDoc.lastAutoTable.finalY + 10;

        if (y > 270) {
          pdfDoc.addPage();
          y = margin;
          drawHeader();
        }
      }

      pdfDoc.save("Feedback_Report.pdf");
      console.log("✅ Feedback report generated.");
      showMessage("✅ Feedback report generated.", "success");
    } catch (error) {
      console.error("❌ Error generating report:", error);
      showMessage("Failed to generate report.", "error");
    }
  };

  const handleSendReport = async () => {
    if (!statusSemester || !statusSection) {
      return showMessage("Select semester and section first", "error");
    }
    try {
      const formQuery = query(
        collection(db, "feedbackForms"),
        where("semester", "==", Number(statusSemester)),
        where("section", "==", statusSection)
      );
      const formSnapshot = await getDocs(formQuery);
      if (formSnapshot.empty) {
        return showMessage("No feedback form found for this semester and section", "error");
      }
      const sortedForms = formSnapshot.docs.sort(
        (a, b) => b.data().createdAt?.seconds - a.data().createdAt?.seconds
      );
      const latestForm = sortedForms[0];
      const feedbackFormId = latestForm.id;
      const subforms = latestForm.data().subforms;
      for (const { subjectCode, teacherName } of subforms) {
        const responseQuery = query(
          collection(db, "feedbackResponses"),
          where("feedbackFormId", "==", feedbackFormId),
          where("subjectCode", "==", subjectCode),
          where("teacherName", "==", teacherName)
        );
        const responseSnap = await getDocs(responseQuery);
        if (responseSnap.empty) continue;
        const totalRatings = {
          C1: 0, C2: 0, C3: 0, C4: 0, C5: 0, C6: 0,
          C7: 0, C8: 0, C9: 0, C10: 0, C11: 0, C12: 0,
        };
        let totalOverall = 0;
        let count = 0;
        responseSnap.forEach((docSnap) => {
          const data = docSnap.data();
          Object.entries(data.ratings).forEach(([c, val]) => {
            totalRatings[c] += val || 0;
          });
          totalOverall += data.total || 0;
          count += 1;
        });
        const subjectReport = {
          total_students: count,
          total: totalOverall,
          percentage: Math.round((totalOverall / (count * 480)) * 100),
        };
        for (const c in totalRatings) {
          subjectReport[c.toLowerCase()] = totalRatings[c]; // C1 → c1
        }
        const teacherReportRef = doc(db, "feedbackReports", teacherName);
        const existingReport = await getDoc(teacherReportRef);
        if (!existingReport.exists()) {
          await setDoc(teacherReportRef, {
            [subjectCode]: subjectReport,
          });
        } else {
          await updateDoc(teacherReportRef, {
            [subjectCode]: subjectReport,
          });
        }
      }
      showMessage("Feedback reports successfully generated and sent to teachers.", "success");
    } catch (err) {
      console.error("Error sending feedback reports:", err);
      showMessage("Something went wrong while generating the report.", "error");
    }
  };

  const handleGenerateForm = async () => {
    if (!semester || !section) {
      return showMessage("Please select a semester and section.", "error");
    }
    setLoading(true);
    try {
      const existingFormQuery = query(collection(db, "feedbackForms"), where("semester", "==", Number(semester)), where("section", "==", section));
      const existingFormSnapshot = await getDocs(existingFormQuery);
      if (!existingFormSnapshot.empty) {
        showMessage(`A form for Semester ${semester} / Section ${section} already exists. Please delete it first.`, "error", 6000);
        setLoading(false);
        return;
      }

      if (subforms.length === 0) {
        showMessage("No teaching assignments found to create a form.", "error");
        setLoading(false);
        return;
      }

      await addDoc(collection(db, "feedbackForms"), {
        semester: Number(semester),
        section,
        createdAt: serverTimestamp(),
        formStatus: "active",
        subforms: subforms,
      });

      showMessage("Feedback form sent successfully!", "success");
      setSemester("");
      setSection("");
      setSubforms([]);
      setPreviewMode(false);
    } catch (error) {
      console.error("Error generating form:", error);
      showMessage("Error sending feedback form.", "error");
    }
    setLoading(false);
  };

  const handleCheckStatus = async () => {
    if (!statusSemester || !statusSection) return showMessage("Please select a semester and section to check status.", "error");
    
    setStatusLoading(true);
    setCurrentPage(1);
    try {
      const formQuery = query(collection(db, "feedbackForms"), where("semester", "==", Number(statusSemester)), where("section", "==", statusSection));
      const formSnapshot = await getDocs(formQuery);
      if (formSnapshot.empty) {
        showMessage("No feedback form found for this semester and section.", "error");
        setStudents([]);
        setStatusLoading(false);
        return;
      }
      const latestForm = formSnapshot.docs.sort((a,b) => b.data().createdAt?.seconds - a.data().createdAt?.seconds)[0];
      const feedbackFormId = latestForm.id;
      
      const studentQuery = query(collection(db, "students"), where("semester", "==", Number(statusSemester)), where("section", "==", statusSection));
      const studentSnapshot = await getDocs(studentQuery);
      const studentList = studentSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));

      const statusList = await Promise.all(
        studentList.map(async (student) => {
          const statusDocRef = doc(db, "feedbackStatus", `${student.id}_${feedbackFormId}`);
          const statusDocSnap = await getDoc(statusDocRef);
          return { ...student, feedbackStatus: statusDocSnap.exists() ? statusDocSnap.data().status : "not_filled" };
        })
      );
      setStudents(statusList);
    } catch (error) {
        showMessage("Something went wrong while checking status.", "error");
        console.error("Error fetching feedback status:", error);
    }
    setStatusLoading(false);
  };

  const filteredStudents = students.filter((student) => showOnlyFilled ? student.feedbackStatus === "filled" : true);
  const totalPages = Math.ceil(filteredStudents.length / ITEMS_PER_PAGE);
  const paginatedStudents = filteredStudents.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  
  const editorTotalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE_EDITOR);
  const editorStartIndex = (editorCurrentPage - 1) * QUESTIONS_PER_PAGE_EDITOR;
  const paginatedQuestions = questions.slice(editorStartIndex, editorStartIndex + QUESTIONS_PER_PAGE_EDITOR);

  return (
    <div className="space-y-8">
      {/* --- CARD 1: CREATE & SEND FEEDBACK FORM --- */}
      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
        <h2 className="text-2xl font-bold mb-2 text-gray-800 flex items-center gap-3"><FaWpforms /> Create & Send Feedback Form</h2>
        <p className="text-sm text-gray-500 mb-6">Select a class, preview the assigned teachers, and send the feedback form to students.</p>

        <StatusMessage message={statusMessage} type={statusMessageType} />
        
        <div className="flex flex-col md:flex-row gap-4 mb-4 items-center">
          <select className="w-full md:w-auto p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" value={semester} onChange={(e) => setSemester(e.target.value)}>
            <option value="">Select Semester</option>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => <option key={sem} value={sem}>{sem}</option>)}
          </select>
          <select className="w-full md:w-auto p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" value={section} onChange={(e) => setSection(e.target.value)}>
            <option value="">Select Section</option>
            {["A", "B", "C"].map((sec) => <option key={sec} value={sec}>{sec}</option>)}
          </select>
          <button
            className="w-full md:w-auto bg-yellow-500 text-white font-bold px-4 py-2 rounded-md whitespace-nowrap hover:bg-yellow-600 transition flex items-center justify-center gap-2"
            onClick={async () => {
              if (!semester || !section) { showMessage("Please select semester and section first.", "error"); return; }
              setLoading(true);
              try {
                const q = query(collection(db, "teachingAssignments"), where("semester", "==", Number(semester)), where("section", "==", section));
                const querySnapshot = await getDocs(q);
                const forms = await Promise.all(querySnapshot.docs.map(async (docSnap) => {
                  const data = docSnap.data();
                  const subjectDocRef = doc(db, "subjects", data.subject);
                  const subjectDocSnap = await getDoc(subjectDocRef);
                  return {
                    subjectCode: data.subject,
                    subjectName: subjectDocSnap.exists() ? subjectDocSnap.data().name : "Unknown Subject",
                    teacherName: data.teacherName,
                  };
                }));
                setSubforms(forms);
                setPreviewMode(true);
              } catch (err) { showMessage("Error loading form preview.", "error"); console.error(err); }
              setLoading(false);
            }}
            disabled={loading}
          >
            <FaSync className={loading ? 'animate-spin' : ''} /> {loading ? "Loading..." : "Preview Form"}
          </button>
        </div>

        {previewMode && (
          <div className="mt-6 border-t pt-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-700">Form Preview: Subjects & Teachers</h3>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm text-left text-gray-700">
                <thead className="text-xs text-white uppercase bg-blue-600">
                  <tr>
                    <th scope="col" className="px-6 py-3">Subject Code</th>
                    <th scope="col" className="px-6 py-3">Subject Name</th>
                    <th scope="col" className="px-6 py-3">Teacher Name</th>
                  </tr>
                </thead>
                <tbody>
                  {subforms.map((form, index) => (
                    <tr key={index} className="bg-white border-b hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">{form.subjectCode}</td>
                      <td className="px-6 py-4">{form.subjectName}</td>
                      <td className="px-6 py-4">{form.teacherName}</td>
                    </tr>
                  ))}
                    {subforms.length === 0 && <tr><td colSpan="3" className="text-center py-6 text-gray-500">No teaching assignments found.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <button onClick={handleGenerateForm} disabled={loading} className="flex items-center gap-2 bg-blue-600 text-white font-bold px-4 py-2 rounded-md whitespace-nowrap hover:bg-blue-700 transition">
                  <FaRegPaperPlane /> {loading ? "Sending..." : "Send Form"}
                </button>
                <button onClick={() => {setShowEditor(!showEditor); setEditorCurrentPage(1);}} className="flex items-center gap-2 bg-gray-600 text-white font-bold px-4 py-2 rounded-md whitespace-nowrap hover:bg-gray-700 transition">
                  <FaEdit /> {showEditor ? "Close Editor" : "Edit Questions"}
                </button>
              </div>
              <button
                onClick={async () => {
                  if (!semester || !section) { showMessage("Please select both semester and section.", "error"); return; }
                  setDeleteLoading(true);
                  try {
                    const formQuery = query(collection(db, "feedbackForms"), where("semester", "==", Number(semester)), where("section", "==", section));
                    const formSnapshot = await getDocs(formQuery);
                    if (formSnapshot.empty) { showMessage("No feedback form found to delete.", "error"); setDeleteLoading(false); return; }
                    const formId = formSnapshot.docs.sort((a,b) => b.data().createdAt?.seconds - a.data().createdAt?.seconds)[0].id;
                    await deleteFormAndRelatedData(formId);
                  } catch (err) { console.error("Error during delete:", err); showMessage("Failed to delete feedback form.", "error"); }
                  finally { setDeleteLoading(false); }
                }}
                disabled={deleteLoading}
                className="flex items-center gap-2 bg-red-600 text-white font-bold px-4 py-2 rounded-md whitespace-nowrap hover:bg-red-700 transition disabled:bg-gray-400"
              >
                <FaTrash /> {deleteLoading ? "Deleting..." : "Delete Old Form"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* --- CARD 2: EDIT FORM QUESTIONS (Conditional) --- */}
      {showEditor && (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-3"><FaEdit /> Edit Form Questions</h2>
            {paginatedQuestions.map((q, index) => {
              const actualIndex = editorStartIndex + index;
              return (
                <div key={actualIndex} className="mb-6 p-4 rounded-lg shadow-sm border border-gray-200 bg-gray-50/30">
                  <h4 className="font-bold text-lg mb-3 text-gray-700">Question {actualIndex + 1}</h4>
                  <textarea rows={2} className="border border-gray-300 p-3 rounded-md w-full mb-4 resize-y" value={q.question}
                    onChange={(e) => {
                      const updated = [...questions];
                      updated[actualIndex].question = e.target.value;
                      setQuestions(updated);
                    }}
                    placeholder="Enter your question"
                  />
                  <label className="block mb-2 text-sm font-medium text-gray-600">Options</label>
                  {q.options?.map((opt, optIndex) => (
                    <div key={optIndex} className="relative flex items-center mb-2">
                      <input type="text" className="border border-gray-300 p-2 rounded-md w-full pr-10" value={opt}
                        onChange={(e) => {
                          const updated = [...questions];
                          updated[actualIndex].options[optIndex] = e.target.value;
                          setQuestions(updated);
                        }}
                      />
                      <button className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-200 hover:bg-red-500 hover:text-white rounded-full flex items-center justify-center text-lg font-bold transition-colors" type="button"
                        onClick={() => {
                          const updated = [...questions];
                          updated[actualIndex].options.splice(optIndex, 1);
                          setQuestions(updated);
                        }}
                        aria-label="Delete option"
                      >&times;</button>
                    </div>
                  ))}
                  <button type="button" className="text-blue-600 hover:underline text-sm font-semibold mt-2"
                    onClick={() => {
                      const updated = [...questions];
                      updated[actualIndex].options.push("");
                      setQuestions(updated);
                    }}
                  >+ Add Option</button>
                  <div className="flex justify-end mt-4">
                    <button className="flex items-center gap-2 text-sm bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-md"
                      onClick={() => {
                        const updated = [...questions];
                        updated.splice(actualIndex, 1);
                        setQuestions(updated);
                        if (paginatedQuestions.length === 1 && editorCurrentPage > 1) {
                          setEditorCurrentPage(editorCurrentPage - 1);
                        }
                      }}
                      type="button"
                    ><FaTrash /> Delete Question</button>
                  </div>
                </div>
              );
            })}

            {editorTotalPages > 1 && <Pagination currentPage={editorCurrentPage} totalPages={editorTotalPages} onPageChange={setEditorCurrentPage} />}

            <div className="flex flex-wrap items-center gap-4 mt-6 border-t pt-6">
              <button
                onClick={() => {
                  const newQuestions = [...questions, { question: "", options: [""] }];
                  setQuestions(newQuestions);
                  setEditorCurrentPage(Math.ceil(newQuestions.length / QUESTIONS_PER_PAGE_EDITOR));
                }}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold px-4 py-2 rounded-md" type="button"
              ><FaPlus /> Add New Question</button>
              <button
                onClick={async () => {
                  try {
                    await setDoc(doc(db, "feedbackTemplates", "default"), { questions, lastUpdated: new Date() });
                    showMessage("Questions updated successfully", "success");
                  } catch (err) {
                    console.error("Error updating questions", err);
                    showMessage("Failed to update questions", "error");
                  }
                }}
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-bold px-4 py-2 rounded-md" type="button"
              ><FaSave /> Save All Questions</button>
            </div>
        </div>
      )}

      {/* --- CARD 3: CHECK FEEDBACK STATUS --- */}
      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
        <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-3"><FaTasks /> Check Feedback Status</h2>
        
        <div className="flex flex-col md:flex-row gap-4 mb-6 items-center">
          <select className="w-full md:w-auto p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" value={statusSemester} onChange={(e) => setStatusSemester(e.target.value)}>
            <option value="">Select Semester</option>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => <option key={sem} value={sem}>{sem}</option>)}
          </select>
          <select className="w-full md:w-auto p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" value={statusSection} onChange={(e) => setStatusSection(e.target.value)}>
            <option value="">Select Section</option>
            {["A", "B", "C"].map((sec) => <option key={sec} value={sec}>{sec}</option>)}
          </select>
          <button onClick={handleCheckStatus} disabled={statusLoading} className="w-full md:w-auto bg-indigo-600 text-white font-bold px-4 py-2 rounded-md whitespace-nowrap hover:bg-indigo-700 transition disabled:bg-gray-400">
            {statusLoading ? "Checking..." : "Check Status"}
          </button>
          <div className="flex-grow"></div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="showFilled" checked={showOnlyFilled} onChange={(e) => { setShowOnlyFilled(e.target.checked); setCurrentPage(1); }} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            <label htmlFor="showFilled" className="text-sm font-medium text-gray-700">Show only filled</label>
          </div>
        </div>

        {students.length > 0 && (
          <div className="border-t pt-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-700">
                <thead className="text-xs text-white uppercase bg-blue-600">
                  <tr>
                    <th scope="col" className="px-6 py-3">Name</th>
                    <th scope="col" className="px-6 py-3">Email</th>
                    <th scope="col" className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedStudents.map(({ id, name, email, feedbackStatus }) => (
                    <tr key={id} className="border-b hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">{name}</td>
                      <td className="px-6 py-4">{email}</td>
                      <td className="px-6 py-4 font-semibold">
                        <span className={`px-2 py-1 rounded-full text-xs ${feedbackStatus === "filled" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                          {feedbackStatus === "filled" ? "Filled" : "Not Filled"}
                        </span>
                      </td>
                    </tr>
                  ))}
                    {paginatedStudents.length === 0 && <tr><td colSpan="3" className="text-center py-6 text-gray-500">No students match the current filter.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex flex-wrap gap-4">
                  <button onClick={handleSendReport} className="flex items-center gap-2 bg-green-700 text-white font-bold px-4 py-2 rounded-md hover:bg-green-800 transition">
                      <FaRegPaperPlane /> Generate & Send Report
                  </button>
                  <button onClick={handleDownloadReport} className="flex items-center gap-2 bg-blue-700 text-white font-bold px-4 py-2 rounded-md hover:bg-blue-800 transition">
                      <FaFilePdf /> Download PDF Report
                  </button>
              </div>
              {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateFeedbackForm;